/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react'
import { SwapOutlined } from '@ant-design/icons'
import {
  HomeTeam,
  VisitingTeam,
} from '@sportninja/common/types/PlayerAttendance'
import {
  snacksContainerStyle,
  snacksImageStyle,
  snacksLabelAndTextContainerStyle,
  snacksLabelStyle,
  snacksTextStyle,
  styledSwapOutlinedStyle,
} from '../styles'
import { getImageThumbnailId } from '@sportninja/common/utils/utils'
import { trackEvent, getPlatformType } from '../../../utils/analytics'

const PLACEHOLDER_PLAYER = '/images/generic_placeholders/placeholder_player.png'

export interface VideographerInfoProps {
  isTeamRepOnTeam: boolean
  team: HomeTeam | VisitingTeam
  teamNameSuffix: string
  onPressManageVideographer: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
  gameId: string
  showVideo: boolean
  teamSide: 'home' | 'visitor'
}

const VideographerInfo: React.FC<VideographerInfoProps> = ({
  isTeamRepOnTeam,
  team,
  teamNameSuffix,
  onPressManageVideographer,
  gameId,
  showVideo,
  teamSide,
}) => {
  const handleClick = useCallback(() => {
    if (!isTeamRepOnTeam) {
      return
    }
    trackEvent('home_game_card_videographer_tapped', {
      platform: getPlatformType(),
      game_id: gameId,
      team_side: teamSide,
      has_assignment: !!team?.videographer_user,
    })
    onPressManageVideographer(
      gameId,
      team?.game_roster_id,
      team?.id,
      team?.name,
      teamSide
    )
  }, [isTeamRepOnTeam, onPressManageVideographer, gameId, team, teamSide])

  const displayText = useMemo(() => {
    const videographer = team?.videographer_user
    if (!videographer) {
      return isTeamRepOnTeam ? 'Choose a Person' : 'Not Set'
    }
    const firstName =
      videographer.name_full ||
      `${videographer.name_first} ${videographer.name_last}`.trim()
    return firstName
  }, [team?.videographer_user, isTeamRepOnTeam])

  const imageSrc = useMemo(() => {
    const videographer = team?.videographer_user
    if (!videographer) {
      return PLACEHOLDER_PLAYER
    }
    return getImageThumbnailId(videographer) || PLACEHOLDER_PLAYER
  }, [team?.videographer_user])

  if (!showVideo) {
    return null
  }

  return (
    <div css={snacksContainerStyle(isTeamRepOnTeam)} onClick={handleClick}>
      <img
        css={snacksImageStyle}
        src={imageSrc}
        alt='Videographer'
        onError={(e) => {
          e.currentTarget.src = PLACEHOLDER_PLAYER
        }}
      />
      <div css={snacksLabelAndTextContainerStyle}>
        <p css={snacksLabelStyle}>Videographer {teamNameSuffix}</p>
        <p css={snacksTextStyle}>{displayText}</p>
      </div>
      {isTeamRepOnTeam ? <SwapOutlined css={styledSwapOutlinedStyle} /> : null}
    </div>
  )
}

export default React.memo(VideographerInfo)
